import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import "./Dashboard.css";
import "./AdminPanel.css";
import ActionPlanPanel from "./ActionPlanPanel";

const CATEGORY_COLORS: Record<string, string> = {
  COMPENSATION: "#4A90E2",
  OCCUPANCY:    "#e84393",
  OPERATING:    "#ea580c",
  ADVERTISING:  "#7c3aed",
  ALLOCATED:    "#475569",
};

const CATEGORY_ORDER = ["COMPENSATION", "OCCUPANCY", "OPERATING", "ADVERTISING", "ALLOCATED"];

// Categories that exist in raw data but should never appear on the dashboard
const EXCLUDED_CATEGORIES = new Set(["CORP_OH", "AMORT", "OTHERADJUSTMENTS"]);

interface AccountRow {
  account: string;
  category: string;
  budget: number;
  actual: number;
  variance: number;
  variancePct: number;
}

interface CategorySummary {
  category: string;
  budget: number;
  actual: number;
  variance: number;
  variancePct: number;
  ytdActual: number;
  ytdBudget: number;
  ytdVariance: number;
}

interface DashboardProps {
  costCenterNumber: string;
  costCenterName?: string;
}

// Which budget dimension is the reference for each month:
//   Apr–Jun  → Annual Budget
//   Jul–Sep  → July Forecast
//   Oct–Dec  → October Forecast
//   Jan–Mar  → January Forecast
const MONTH_BUDGET_DIM: Record<string, string> = {
  April: "Annual Budget",   May: "Annual Budget",      June: "Annual Budget",
  July:  "July Forecast",   August: "July Forecast",   September: "July Forecast",
  October: "October Forecast", November: "October Forecast", December: "October Forecast",
  January: "January Forecast", February: "January Forecast", March: "January Forecast",
};

// All unique budget dimensions we might need
const BUDGET_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"] as const;

// Accounts written by syncComputedBudgets — used to detect phantom forecast rows
const COMPUTED_ACCT_NUMS = new Set(["60010", "60022", "60100", "60120", "60130"]);

/** True if a budget dimension has non-zero data for accounts beyond the 5 salary-computed ones */
function dimHasUploadedData(dimData: Record<string, any[]>, autoCompute = true): boolean {
  return Object.entries(dimData).some(([account, entries]) => {
    const num = account.split(" - ")[0].trim();
    if (autoCompute && COMPUTED_ACCT_NUMS.has(num)) return false;
    return entries.some((e: any) => (e.budget ?? 0) !== 0);
  });
}

const FISCAL_MONTHS_ALL = [
  "April","May","June","July","August","September",
  "October","November","December","January","February","March",
];
const FISCAL_MONTHS_SHORT = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];

// Quarter mapping: which months belong to each fiscal quarter
const MONTH_TO_QUARTER: Record<string, { label: string; months: string[] }> = {
  April:     { label: "Q1", months: ["April", "May", "June"] },
  May:       { label: "Q1", months: ["April", "May", "June"] },
  June:      { label: "Q1", months: ["April", "May", "June"] },
  July:      { label: "Q2", months: ["July", "August", "September"] },
  August:    { label: "Q2", months: ["July", "August", "September"] },
  September: { label: "Q2", months: ["July", "August", "September"] },
  October:   { label: "Q3", months: ["October", "November", "December"] },
  November:  { label: "Q3", months: ["October", "November", "December"] },
  December:  { label: "Q3", months: ["October", "November", "December"] },
  January:   { label: "Q4", months: ["January", "February", "March"] },
  February:  { label: "Q4", months: ["January", "February", "March"] },
  March:     { label: "Q4", months: ["January", "February", "March"] },
};

const Dashboard: React.FC<DashboardProps> = ({ costCenterNumber, costCenterName }) => {
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [availableFYs, setAvailableFYs] = useState<string[]>([]);
  const [selectedFY, setSelectedFY] = useState<string>("");
  // budgetsByDim["Annual Budget"][account] = BudgetItem[]
  const [budgetsByDim, setBudgetsByDim] = useState<Record<string, Record<string, any[]>>>({});
  const [actualsRaw, setActualsRaw] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [chartType, setChartType] = useState<"bar" | "line">("bar");
  const [chartCollapsed, setChartCollapsed] = useState(true);
  const [hasBudgetData, setHasBudgetData] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [actionPlanOpen, setActionPlanOpen] = useState(false);
  const [actionPlanCount, setActionPlanCount] = useState<{ total: number; open: number }>({ total: 0, open: 0 });
  const [salaryAutoCompute, setSalaryAutoCompute] = useState(true);
  const [adminComment, setAdminComment] = useState("");
  const [dashAcctSearch, setDashAcctSearch] = useState("");
  const [dashAcctTags, setDashAcctTags] = useState<string[]>([]);
  const [dashAcctDropOpen, setDashAcctDropOpen] = useState(false);
  const [dashSimpleView, setDashSimpleView] = useState(false);
  const dashAcctInputRef = React.useRef<HTMLInputElement>(null);
  const dashAcctDropRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dashAcctDropOpen) return;
    const handler = (e: MouseEvent) => {
      if (dashAcctDropRef.current && !dashAcctDropRef.current.contains(e.target as Node)) setDashAcctDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dashAcctDropOpen]);

  useEffect(() => {
    if (!selectedFY || !selectedMonth || !costCenterNumber) { setAdminComment(""); return; }
    api.get("/settings/monthly-comments", { params: { fy: selectedFY, month: selectedMonth } })
      .then(r => {
        const comments: Record<string, string> = r.data.comments || {};
        setAdminComment(comments[costCenterNumber] || "");
      })
      .catch(() => setAdminComment(""));
  }, [selectedFY, selectedMonth, costCenterNumber]);

  useEffect(() => {
    api.get("/settings/forecast-config")
      .then(res => { setSalaryAutoCompute(res.data.salary_auto_compute !== false); })
      .catch(() => {});
  }, []);

  // Fetch available FYs on mount (no FY filter)
  useEffect(() => {
    if (!costCenterNumber) return;
    api.get("/budgets/months-with-actuals", { params: { cost_center: costCenterNumber } })
      .then(res => {
        const fys: string[] = res.data.fiscal_years || [];
        setAvailableFYs(fys);
        if (fys.length > 0 && !selectedFY) setSelectedFY(fys[0]); // default to most recent
      })
      .catch(err => console.error("Failed to fetch FYs:", err));
  }, [costCenterNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch which months have actuals (filtered by selected FY)
  useEffect(() => {
    if (!costCenterNumber || !selectedFY) return;
    api.get("/budgets/months-with-actuals", { params: { cost_center: costCenterNumber, fy: selectedFY } })
      .then(res => {
        const months: string[] = res.data.months || [];
        setAvailableMonths(months);
        if (months.length > 0) setSelectedMonth(months[months.length - 1]);
        else setSelectedMonth("");
      })
      .catch(err => console.error("Failed to fetch actuals months:", err));
  }, [costCenterNumber, selectedFY]);

  // Fetch all budget dimensions + Actuals (re-fetch when FY changes)
  useEffect(() => {
    if (!costCenterNumber || !selectedFY) return;
    setLoading(true);
    Promise.all([
      ...BUDGET_DIMS.map(dim =>
        api.get("/budgets", { params: { cost_center: costCenterNumber, dimension: dim, fy: selectedFY } })
      ),
      api.get("/budgets", { params: { cost_center: costCenterNumber, dimension: "Actuals", fy: selectedFY } }),
    ])
      .then(responses => {
        const dimMap: Record<string, Record<string, any[]>> = {};
        BUDGET_DIMS.forEach((dim, i) => { dimMap[dim] = responses[i].data; });
        setBudgetsByDim(dimMap);
        setActualsRaw(responses[BUDGET_DIMS.length].data);
        // Check if budget data actually has non-zero values
        const annualBudget = dimMap["Annual Budget"] || {};
        const hasAny = Object.values(annualBudget).some(months =>
          months.some((m: any) => m.budget !== 0)
        );
        setHasBudgetData(hasAny);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch dashboard data:", err);
        setLoading(false);
      });
  }, [costCenterNumber, selectedFY]);

  // Reset category/account filter when month changes
  useEffect(() => { setSelectedCategory(null); setSelectedAccount(null); }, [selectedMonth]);
  // Reset account filter when category filter changes
  useEffect(() => { setSelectedAccount(null); }, [selectedCategory]);

  // Fetch action plan count for current month
  const fetchActionCount = useCallback(() => {
    if (!costCenterNumber || !selectedFY || !selectedMonth || selectedMonth === "YTD") {
      setActionPlanCount({ total: 0, open: 0 });
      return;
    }
    api.get("/action-plans", { params: { cost_center: costCenterNumber, fy: selectedFY, month: selectedMonth } })
      .then(res => {
        const items = res.data as { status: string }[];
        setActionPlanCount({ total: items.length, open: items.filter(a => a.status === "OPEN").length });
      })
      .catch(() => setActionPlanCount({ total: 0, open: 0 }));
  }, [costCenterNumber, selectedFY, selectedMonth]);

  useEffect(() => { fetchActionCount(); }, [fetchActionCount]);

  // For YTD, sum across all available months; otherwise use the single selected month
  const monthsToSum = selectedMonth === "YTD" ? availableMonths : (selectedMonth ? [selectedMonth] : []);

  // Per-account rows — budget reference is the correct dimension for each month
  const accountRows: AccountRow[] = useMemo(() => {
    if (monthsToSum.length === 0) return [];
    const allAccounts = Array.from(new Set([
      ...Object.values(budgetsByDim).flatMap(d => Object.keys(d)),
      ...Object.keys(actualsRaw),
    ]));
    return allAccounts
      .map(account => {
        let budget = 0, actual = 0;
        let category = "OTHER";
        for (const month of monthsToSum) {
          const dim = MONTH_BUDGET_DIM[month] ?? "Annual Budget";
          const dimData = budgetsByDim[dim] ?? {};
          const budgetEntry = dimData[account]?.find((m: any) => m.month === month);
          const actualEntry = actualsRaw[account]?.find((m: any) => m.month === month);
          budget += Number(budgetEntry?.budget) || 0;
          actual += Number(actualEntry?.budget) || 0;
          if (category === "OTHER") {
            category = (budgetEntry?.category || actualEntry?.category || "OTHER").toUpperCase();
          }
        }
        let ytdActual = 0, ytdBudget = 0;
        for (const month of availableMonths) {
          const ae = actualsRaw[account]?.find((m: any) => m.month === month);
          ytdActual += Number(ae?.budget) || 0;
          const dim = MONTH_BUDGET_DIM[month] ?? "Annual Budget";
          const dimData = budgetsByDim[dim] ?? {};
          const entry = dimData[account]?.find((e: any) => e.month === month);
          ytdBudget += Number(entry?.budget) || 0;
        }
        const variance    = budget - actual;
        const variancePct = budget !== 0 ? (variance / Math.abs(budget)) * 100 : 0;
        const ytdVariance = ytdBudget - ytdActual;
        return { account, category, budget, actual, variance, variancePct, ytdActual, ytdBudget, ytdVariance };
      })
      .filter(r => !EXCLUDED_CATEGORIES.has(r.category))
      .sort((a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.category);
        const bi = CATEGORY_ORDER.indexOf(b.category);
        const catDiff = (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        return catDiff !== 0 ? catDiff : a.account.localeCompare(b.account);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetsByDim, actualsRaw, selectedMonth, availableMonths]);

  // Category summaries (excluded categories already filtered out of accountRows)
  const categorySummaries: CategorySummary[] = useMemo(() => {
    const map: Record<string, { budget: number; actual: number; ytdActual: number; ytdBudget: number }> = {};
    accountRows.forEach(({ category, budget, actual, ytdActual, ytdBudget }) => {
      if (!map[category]) map[category] = { budget: 0, actual: 0, ytdActual: 0, ytdBudget: 0 };
      map[category].budget += budget;
      map[category].actual += actual;
      map[category].ytdActual += ytdActual;
      map[category].ytdBudget += ytdBudget;
    });
    return Object.entries(map)
      .sort(([a], [b]) => {
        const ai = CATEGORY_ORDER.indexOf(a);
        const bi = CATEGORY_ORDER.indexOf(b);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      })
      .map(([category, { budget, actual, ytdActual, ytdBudget }]) => {
        const variance    = budget - actual;
        const variancePct = budget !== 0 ? (variance / Math.abs(budget)) * 100 : 0;
        const ytdVariance = ytdBudget - ytdActual;
        return { category, budget, actual, variance, variancePct, ytdActual, ytdBudget, ytdVariance };
      });
  }, [accountRows]);

  // Rows filtered by selected category (for the tables)
  const filteredRows = useMemo(() =>
    selectedCategory ? accountRows.filter(r => r.category === selectedCategory) : accountRows,
    [accountRows, selectedCategory]
  );

  // Per-account detail rows with quarterly budget, QTD utilization %, and YTG
  const accountDetailRows = useMemo(() => {
    if (!selectedMonth || selectedMonth === "YTD" || !Object.keys(budgetsByDim).length) return accountRows.map(r => ({ ...r, qtrBudget: r.budget, utilPct: r.budget > 0 ? (r.actual / r.budget) * 100 : 0, ytgBudget: 0, annualBudget: 0 }));
    const qtr = MONTH_TO_QUARTER[selectedMonth];
    if (!qtr) return accountRows.map(r => ({ ...r, qtrBudget: r.budget, utilPct: 0, ytgBudget: 0, annualBudget: 0 }));
    const currentMonthIdx = FISCAL_MONTHS_ALL.indexOf(selectedMonth);
    // Remaining months from current month to end of FY (inclusive of current)
    const ytgMonths = FISCAL_MONTHS_ALL.slice(currentMonthIdx);
    // Quarter months up to and including selected month (QTD)
    const qtdMonths = qtr.months.filter(m => {
      const mIdx = FISCAL_MONTHS_ALL.indexOf(m);
      return mIdx <= currentMonthIdx;
    });

    return accountRows.map(row => {
      let qtrBudget = 0;
      let ytgBudget = 0;
      let qtdActual = 0;
      // Sum quarter budget across all quarter months
      for (const m of qtr.months) {
        const dim = MONTH_BUDGET_DIM[m] ?? "Annual Budget";
        const dimData = budgetsByDim[dim] ?? {};
        const entry = dimData[row.account]?.find((e: any) => e.month === m);
        qtrBudget += Number(entry?.budget) || 0;
      }
      // Sum QTD actuals (quarter months up to selected month)
      for (const m of qtdMonths) {
        if (availableMonths.includes(m)) {
          const ae = actualsRaw[row.account]?.find((e: any) => e.month === m);
          qtdActual += Number(ae?.budget) || 0;
        }
      }
      // Sum YTG budget from current month to end of FY
      for (const m of ytgMonths) {
        const dim = MONTH_BUDGET_DIM[m] ?? "Annual Budget";
        const dimData = budgetsByDim[dim] ?? {};
        const entry = dimData[row.account]?.find((e: any) => e.month === m);
        ytgBudget += Number(entry?.budget) || 0;
      }
      // Sum annual budget across all 12 fiscal months
      let annualBudget = 0;
      for (const m of FISCAL_MONTHS_ALL) {
        const dim = MONTH_BUDGET_DIM[m] ?? "Annual Budget";
        const dimData = budgetsByDim[dim] ?? {};
        const entry = dimData[row.account]?.find((e: any) => e.month === m);
        annualBudget += Number(entry?.budget) || 0;
      }
      const utilPct = qtrBudget > 0 ? (qtdActual / qtrBudget) * 100 : 0;
      return { ...row, qtrBudget, utilPct, ytgBudget, annualBudget };
    });
  }, [accountRows, selectedMonth, budgetsByDim, actualsRaw, availableMonths]);

  // Most Probable Year (MPY) — full-year total from the latest forecast dimension
  const { mpyTotal, mpyDimLabel, annualBudgetTotal, mpyByAcct, abByAcct } = useMemo(() => {
    const allAccounts = Array.from(new Set(
      Object.values(budgetsByDim).flatMap(d => Object.keys(d))
    ));

    const FISCAL_ORDER = ["April","May","June","July","August","September","October","November","December","January","February","March"];
    const latestActuals = availableMonths.length > 0 ? availableMonths[availableMonths.length - 1] : "";
    const latestIdx = FISCAL_ORDER.indexOf(latestActuals);
    let targetDim: string;
    if (latestIdx <= 2)       targetDim = "July Forecast";
    else if (latestIdx <= 5)  targetDim = "October Forecast";
    else                      targetDim = "January Forecast";
    const fallbackChain = [targetDim, "July Forecast", "Annual Budget"];
    let latestDim = "Annual Budget";
    for (const dim of fallbackChain) {
      if (dimHasUploadedData(budgetsByDim[dim] ?? {}, salaryAutoCompute)) {
        latestDim = dim;
        break;
      }
    }

    let mpySum = 0;
    const mpyPerAcct: Record<string, number> = {};
    const abPerAcct: Record<string, number> = {};
    const latestData = budgetsByDim[latestDim] ?? {};
    const abFallback = budgetsByDim["Annual Budget"] ?? {};
    const actualsSet = new Set(availableMonths);
    for (const month of FISCAL_MONTHS_ALL) {
      for (const acc of allAccounts) {
        const accNum = acc.split(" - ")[0].trim();
        const isComputed = salaryAutoCompute && COMPUTED_ACCT_NUMS.has(accNum);
        const useActual = isComputed && actualsSet.has(month);

        let val = 0;
        let cat = "OTHER";
        if (useActual) {
          const ae = actualsRaw[acc]?.find((e: any) => e.month === month);
          val = Number(ae?.budget) || 0;
          cat = (ae?.category || "OTHER").toUpperCase();
          if (!cat || cat === "OTHER") {
            const abEntry = abFallback[acc]?.find((e: any) => e.month === month);
            cat = (abEntry?.category || "OTHER").toUpperCase();
          }
        } else {
          const entry = latestData[acc]?.find((e: any) => e.month === month)
            || abFallback[acc]?.find((e: any) => e.month === month);
          if (!entry) continue;
          val = Number(entry.budget) || 0;
          cat = (entry.category || "OTHER").toUpperCase();
        }
        if (val !== 0) {
          mpyPerAcct[acc] = (mpyPerAcct[acc] || 0) + val;
          if ((!selectedCategory || cat === selectedCategory) && (!selectedAccount || acc === selectedAccount)) {
            mpySum += val;
          }
        }
      }
    }

    let abSum = 0;
    const abDimData = budgetsByDim["Annual Budget"] ?? {};
    for (const month of FISCAL_MONTHS_ALL) {
      for (const acc of allAccounts) {
        const entry = abDimData[acc]?.find((e: any) => e.month === month);
        if (!entry) continue;
        const cat = (entry.category || "OTHER").toUpperCase();
        const val = Number(entry.budget) || 0;
        abPerAcct[acc] = (abPerAcct[acc] || 0) + val;
        if ((!selectedCategory || cat === selectedCategory) && (!selectedAccount || acc === selectedAccount)) {
          abSum += val;
        }
      }
    }

    return { mpyTotal: mpySum, mpyDimLabel: latestDim, annualBudgetTotal: abSum, mpyByAcct: mpyPerAcct, abByAcct: abPerAcct };
  }, [budgetsByDim, actualsRaw, selectedCategory, selectedAccount, availableMonths]);

  // ── Category variance table data (ActualsDashboard style) ────────────────
  const categoryVarData = useMemo(() => {
    // Compute YTD actual and YTD budget per account (sum across ALL available months)
    const ytdActualByAcct: Record<string, number> = {};
    const ytdBudgetByAcct: Record<string, number> = {};
    const MONTH_BUDGET_DIM_LOCAL: Record<string, string> = {
      April: "Annual Budget", May: "Annual Budget", June: "Annual Budget",
      July: "July Forecast", August: "July Forecast", September: "July Forecast",
      October: "October Forecast", November: "October Forecast", December: "October Forecast",
      January: "January Forecast", February: "January Forecast", March: "January Forecast",
    };
    const allAccts = Array.from(new Set([
      ...Object.values(budgetsByDim).flatMap(d => Object.keys(d)),
      ...Object.keys(actualsRaw),
    ]));
    for (const account of allAccts) {
      let ytdA = 0, ytdB = 0;
      for (const month of availableMonths) {
        const ae = actualsRaw[account]?.find((m: any) => m.month === month);
        ytdA += Number(ae?.budget) || 0;
        const dim = MONTH_BUDGET_DIM_LOCAL[month] ?? "Annual Budget";
        const dimData = budgetsByDim[dim] ?? {};
        const entry = dimData[account]?.find((e: any) => e.month === month);
        ytdB += Number(entry?.budget) || 0;
      }
      ytdActualByAcct[account] = ytdA;
      ytdBudgetByAcct[account] = ytdB;
    }

    return CATEGORY_ORDER
      .filter(cat => accountDetailRows.some(r => r.category === cat))
      .map(cat => {
        const rows = accountDetailRows.filter(r => r.category === cat);
        const budget       = rows.reduce((s, r) => s + r.budget, 0);
        const actual       = rows.reduce((s, r) => s + r.actual, 0);
        const qtrBudget    = rows.reduce((s, r) => s + r.qtrBudget, 0);
        const ytgBudget    = rows.reduce((s, r) => s + r.ytgBudget, 0);
        const annualBudget = rows.reduce((s, r) => s + r.annualBudget, 0);
        const ytdActual = rows.reduce((s, r) => s + (ytdActualByAcct[r.account] || 0), 0);
        const ytdBudget = rows.reduce((s, r) => s + (ytdBudgetByAcct[r.account] || 0), 0);
        const variance  = budget - actual;
        const ytdVariance = ytdBudget - ytdActual;
        const utilBase  = selectedMonth === "YTD" ? budget : qtrBudget;
        const utilPct   = utilBase > 0 ? (ytdActual / utilBase) * 100 : 0;
        const catMPY = rows.reduce((s, r) => s + (mpyByAcct[r.account] || 0), 0);
        const catAnnBudget = rows.reduce((s, r) => s + (abByAcct[r.account] || 0), 0);
        const catMPYVariance = catAnnBudget - catMPY;

        // Account breakdown bar: non-zero variance accounts, sorted by |variance|
        const acctVars = rows
          .map(r => ({ account: r.account, variance: r.budget - r.actual }))
          .filter(a => a.variance !== 0)
          .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
        const totalAbsVar = acctVars.reduce((s, a) => s + Math.abs(a.variance), 0) || 1;

        return { category: cat, budget, actual, variance, ytdActual, ytdBudget, ytdVariance, qtrBudget, ytgBudget, utilPct, catAnnBudget, catMPY, catMPYVariance, acctVars, totalAbsVar, accounts: rows };
      });
  }, [accountDetailRows, budgetsByDim, actualsRaw, availableMonths, selectedMonth, mpyByAcct, abByAcct]);

  const dashAllAccounts = useMemo(() => {
    const seen = new Set<string>();
    return categoryVarData.flatMap(cat => cat.accounts.map(r => ({ account: r.account, category: cat.category }))).filter(a => { if (seen.has(a.account)) return false; seen.add(a.account); return true; });
  }, [categoryVarData]);

  const dashAcctSuggestions = useMemo(() => {
    const q = dashAcctSearch.trim().toLowerCase();
    if (!q) return dashAllAccounts.slice(0, 20);
    return dashAllAccounts.filter(a => a.account.toLowerCase().includes(q)).slice(0, 20);
  }, [dashAcctSearch, dashAllAccounts]);

  // Quarterly label for header
  const quarterLabel = useMemo(() => {
    if (!selectedMonth || selectedMonth === "YTD") return null;
    return MONTH_TO_QUARTER[selectedMonth]?.label ?? null;
  }, [selectedMonth]);

  // ── Spend trend chart data ───────────────────────────────────────────────
  const spendChartMonthly = useMemo(() => {
    if (!Object.keys(budgetsByDim).length) return [];
    const allAccounts = Array.from(new Set([
      ...Object.values(budgetsByDim).flatMap(d => Object.keys(d)),
      ...Object.keys(actualsRaw),
    ]));
    const annualDimData = budgetsByDim["Annual Budget"] ?? {};
    // Pre-check which forecast dimensions have real uploaded data (not just salary-computed)
    const uploadedDims = new Set<string>(["Annual Budget"]);
    for (const dim of BUDGET_DIMS) {
      if (dim !== "Annual Budget" && dimHasUploadedData(budgetsByDim[dim] ?? {}, salaryAutoCompute)) {
        uploadedDims.add(dim);
      }
    }
    return FISCAL_MONTHS_ALL.map((month, idx) => {
      // Pick the appropriate forecast dimension for this specific month
      const rawForecastDim = MONTH_BUDGET_DIM[month] ?? "Annual Budget";
      // Only use the forecast dimension if it was actually uploaded
      const forecastDim = uploadedDims.has(rawForecastDim) ? rawForecastDim : "Annual Budget";
      const forecastDimData = budgetsByDim[forecastDim] ?? {};
      let mBudgetAnnual = 0, mBudgetForecast = 0, mActual = 0;
      const hasActuals = availableMonths.includes(month);
      for (const acc of allAccounts) {
        const beForecast = forecastDimData[acc]?.find((e: any) => e.month === month);
        const beAnnual   = annualDimData[acc]?.find((e: any) => e.month === month);
        const ae         = hasActuals ? actualsRaw[acc]?.find((e: any) => e.month === month) : undefined;
        const category = ((beForecast?.category || beAnnual?.category || ae?.category) ?? "OTHER").toUpperCase();
        if (EXCLUDED_CATEGORIES.has(category)) continue;
        if (selectedCategory && category !== selectedCategory) continue;
        if (selectedAccount && acc !== selectedAccount) continue;
        mBudgetAnnual   += Number(beAnnual?.budget) || 0;
        mBudgetForecast += Number(beForecast?.budget) || 0;
        if (hasActuals) mActual += Number(ae?.budget) || 0;
      }
      return { month, short: FISCAL_MONTHS_SHORT[idx], forecastDim, idx, mBudgetAnnual, mBudgetForecast, mActual: hasActuals ? mActual : null };
    });
  }, [budgetsByDim, actualsRaw, availableMonths, selectedCategory, selectedAccount]);

  // Reference dimension label for the header
  const refDimLabel = useMemo(() => {
    if (!selectedMonth) return null;
    if (selectedMonth === "YTD") {
      const dimsRaw = availableMonths.map(m => MONTH_BUDGET_DIM[m] ?? "Annual Budget");
      const dims = dimsRaw.filter((d, i) => dimsRaw.indexOf(d) === i);
      return dims.length > 1 ? `${dims[0]} → ${dims[dims.length - 1]}` : (dims[0] ?? null);
    }
    return MONTH_BUDGET_DIM[selectedMonth] ?? "Annual Budget";
  }, [selectedMonth, availableMonths]);

  const refWord = "Forecast";

  // Totals
  const totalBudget   = categorySummaries.reduce((s, c) => s + c.budget, 0);
  const totalActual   = categorySummaries.reduce((s, c) => s + c.actual, 0);
  const totalVariance = totalBudget - totalActual;

  // Full-year budget (all 12 months) for utilization % denominator
  const fullYearBudget = useMemo(() => {
    const allAccounts = Array.from(new Set(
      Object.values(budgetsByDim).flatMap(d => Object.keys(d))
    ));
    let total = 0;
    for (const month of FISCAL_MONTHS_ALL) {
      const dim = MONTH_BUDGET_DIM[month] ?? "Annual Budget";
      const dimData = budgetsByDim[dim] ?? {};
      for (const acc of allAccounts) {
        const entry = dimData[acc]?.find((e: any) => e.month === month);
        if (!entry) continue;
        const cat = (entry.category || "OTHER").toUpperCase();
        if (EXCLUDED_CATEGORIES.has(cat)) continue;
        if (selectedCategory && cat !== selectedCategory) continue;
        if (selectedAccount && acc !== selectedAccount) continue;
        total += Number(entry.budget) || 0;
      }
    }
    return total;
  }, [budgetsByDim, selectedCategory, selectedAccount]);

  const totalUtilPct  = fullYearBudget > 0 ? (totalActual / fullYearBudget) * 100 : 0;

  // YTD totals — use a SINGLE reference forecast dimension for all YTD months
  // so prior months (where actuals were baked into the forecast) show ~zero variance.
  const ytdActual = spendChartMonthly.reduce((s, m) => s + (m.mActual ?? 0), 0);
  const ytdBudget = useMemo(() => {
    const ytdMonths = spendChartMonthly.filter(m => m.mActual !== null);
    if (ytdMonths.length === 0) return 0;
    const latestMonth = ytdMonths[ytdMonths.length - 1].month;
    const refDim = MONTH_BUDGET_DIM[latestMonth] ?? "Annual Budget";
    const refDimData = budgetsByDim[refDim] ?? {};
    const allAccounts = Array.from(new Set([
      ...Object.values(budgetsByDim).flatMap(d => Object.keys(d)),
      ...Object.keys(actualsRaw),
    ]));
    let total = 0;
    for (const m of ytdMonths) {
      for (const acc of allAccounts) {
        const be = refDimData[acc]?.find((e: any) => e.month === m.month);
        const category = ((be?.category) ?? "OTHER").toUpperCase();
        if (EXCLUDED_CATEGORIES.has(category)) continue;
        if (selectedCategory && category !== selectedCategory) continue;
        if (selectedAccount && acc !== selectedAccount) continue;
        total += Number(be?.budget) || 0;
      }
    }
    return total;
  }, [spendChartMonthly, budgetsByDim, actualsRaw, selectedCategory, selectedAccount]);
  const ytdVariance = ytdBudget - ytdActual;
  const ytdIsOver = ytdVariance < 0;

  // Top 5 unfavorable: actuals > budget (variance most negative)
  const top5Unfavorable = [...filteredRows]
    .filter(r => r.variance < 0)
    .sort((a, b) => a.variance - b.variance)
    .slice(0, 5);

  // Top 5 favorable: actuals < budget (variance most positive)
  const top5Favorable = [...filteredRows]
    .filter(r => r.variance > 0)
    .sort((a, b) => b.variance - a.variance)
    .slice(0, 5);

  // Formatting helpers
  const fmt = (v: number) => {
    const str = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.abs(v));
    return v < 0 ? `(${str})` : str;
  };

  const fmtVar = (n: number) => {
    const str = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.abs(n));
    return n < 0 ? `(${str})` : str;
  };

  // Subtitle text for header portal
  const subtitleText = selectedMonth === "YTD" && availableMonths.length > 0
    ? `${selectedFY} YTD (${availableMonths[0]} – ${availableMonths[availableMonths.length - 1]}) — ${refWord} vs Actuals`
    : selectedMonth
    ? `${selectedFY} ${selectedMonth} — ${refWord} vs Actuals`
    : selectedFY
    ? `${selectedFY} — Select a month to view`
    : "Select a fiscal year to begin";

  const subtitleSlot = document.getElementById("page-subtitle-slot");
  const controlsSlot = document.getElementById("page-title-controls-slot");

  const pickerControls = (
    <div className="dash-controls-bar">
      <div className="dash-controls-pickers">
        {availableFYs.length > 0 && (
          <div className="dash-month-picker">
            <span className="dash-month-label">FY</span>
            <select
              value={selectedFY}
              onChange={e => setSelectedFY(e.target.value)}
              className="dash-month-select"
            >
              {availableFYs.map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
          </div>
        )}
        <div className="dash-month-picker">
          <span className="dash-month-label">Month</span>
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className="dash-month-select"
          >
            {availableMonths.length === 0 && <option value="">No actuals available</option>}
            {availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
            {availableMonths.length > 0 && (
              <option value="YTD">YTD ({availableMonths[0]}–{availableMonths[availableMonths.length - 1]})</option>
            )}
          </select>
        </div>
      </div>
      {selectedMonth && selectedMonth !== "YTD" && (
        <button className="ap-trigger" onClick={() => setActionPlanOpen(true)}>
          <span className="material-icons">checklist</span>
          Action Plan
          {actionPlanCount.total > 0 && (
            <span className={`ap-trigger-badge${actionPlanCount.open > 0 ? " ap-trigger-badge--has-open" : ""}`}>
              {actionPlanCount.open > 0 ? `${actionPlanCount.open} open` : `${actionPlanCount.total}`}
            </span>
          )}
        </button>
      )}
    </div>
  );

  return (
    <div className="dash-container">

      {/* Portal subtitle into header row 2 */}
      {subtitleSlot && createPortal(
        <span className="app-header-page-subtitle">{subtitleText}</span>,
        subtitleSlot
      )}

      {/* Portal FY/Month pickers into header row 2 */}
      {controlsSlot && createPortal(pickerControls, controlsSlot)}

      {loading && (
        <div className="dash-loading">
          <span className="material-icons dash-loading-icon">hourglass_top</span>
          Loading dashboard data…
        </div>
      )}

      {/* ── No actuals empty state ───────────────────────────────────────── */}
      {!loading && !selectedMonth && selectedFY && (
        <div className="dash-info-banner">
          <span className="material-icons">info</span>
          <span>No actuals data available for <strong>{selectedFY}</strong>. Upload monthly actuals from the <strong>Data Upload</strong> tab to see budget vs actual comparisons.</span>
        </div>
      )}

      {!loading && selectedMonth && (
        <>
          {/* ── No budget data warning ────────────────────────────────────── */}
          {!hasBudgetData && (
            <div className="dash-info-banner dash-info-banner--warn">
              <span className="material-icons">warning</span>
              <span>No Annual Budget data found for <strong>{selectedFY}</strong>. Forecast columns will show $0. Upload a budget from the <strong>Data Upload</strong> tab.</span>
            </div>
          )}

          {/* ── Budget reference dimension ── */}
          <div className="dash-kpi-ref">Forecast Reference: {refDimLabel ?? "Annual Budget"} {selectedFY ? selectedFY.toUpperCase() : ""}</div>

          {/* ── KPI Summary Strip (matches ActualsDashboard style) ─────── */}
          <div className="dash-kpi-strip">
            <div className="dash-kpi-card">
              <div className="dash-kpi-label">
                <span className="material-icons">account_balance</span>
                {selectedMonth === "YTD" ? "YTD" : selectedMonth} {refWord}
              </div>
              <div className={`dash-kpi-value${totalBudget < 0 ? " dash-kpi-value--red" : ""}`}>{fmt(totalBudget)}</div>
              <div className="dash-kpi-sub">{refDimLabel ?? "Forecast reference"}</div>
            </div>
            <div className="dash-kpi-card">
              <div className="dash-kpi-label">
                <span className="material-icons">payments</span>
                {selectedMonth === "YTD" ? "YTD" : selectedMonth} Actual
              </div>
              <div className={`dash-kpi-value${totalActual < 0 ? " dash-kpi-value--red" : ""}`}>{fmt(totalActual)}</div>
              <div className="dash-kpi-sub">
                {selectedMonth === "YTD" ? `Apr \u2013 ${availableMonths[availableMonths.length - 1] || "\u2014"}` : `${selectedMonth} actual spend`}
              </div>
            </div>
            <div className={`dash-kpi-card ${totalVariance < 0 ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
              <div className={`dash-kpi-label ${totalVariance < 0 ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                <span className="material-icons">{totalVariance < 0 ? "warning" : "check_circle"}</span>
                {totalVariance < 0 ? `Over ${refWord}` : `Under ${refWord}`}
              </div>
              <div className={`dash-kpi-value ${totalVariance < 0 ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                {fmtVar(totalVariance)}
              </div>
              <div className={`dash-kpi-sub ${totalVariance < 0 ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                {selectedMonth === "YTD" ? "YTD" : selectedMonth} actual vs {refWord.toLowerCase()}
              </div>
            </div>
            <div className="dash-kpi-card">
              <div className="dash-kpi-label">
                <span className="material-icons">payments</span>
                YTD Actuals
              </div>
              <div className="dash-kpi-value">{fmt(ytdActual)}</div>
              <div className="dash-kpi-sub">Year-to-date spend</div>
            </div>
            <div className={`dash-kpi-card ${ytdIsOver ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
              <div className={`dash-kpi-label ${ytdIsOver ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                <span className="material-icons">{ytdIsOver ? "warning" : "check_circle"}</span>
                YTD Variance
              </div>
              <div className={`dash-kpi-value ${ytdIsOver ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                {fmtVar(ytdVariance)}
              </div>
              <div className={`dash-kpi-sub ${ytdIsOver ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                YTD actual vs {refWord.toLowerCase()}
              </div>
            </div>
            <div className="dash-kpi-card">
              <div className="dash-kpi-label">
                <span className="material-icons">compare_arrows</span>
                MPY vs Annual Budget
              </div>
              <div className="dash-kpi-vs">
                <span className="dash-kpi-value">{fmt(mpyTotal)}</span>
                <div className="dash-kpi-vs-row2">
                  <span className="dash-kpi-vs-sep">vs</span>
                  <span className="dash-kpi-vs-secondary">{fmt(annualBudgetTotal)}</span>
                </div>
              </div>
              <div className="dash-kpi-sub">{mpyDimLabel} vs Annual Budget</div>
            </div>
            {(() => {
              const mpyVar = annualBudgetTotal - mpyTotal;
              const mpyIsOver = mpyVar < 0;
              return (
                <div className={`dash-kpi-card ${mpyIsOver ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
                  <div className={`dash-kpi-label ${mpyIsOver ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                    MPY Variance
                  </div>
                  <div className={`dash-kpi-value ${mpyIsOver ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                    {fmtVar(mpyVar)}
                  </div>
                  <div className={`dash-kpi-sub ${mpyIsOver ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                    Projected vs Annual Budget
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── Collapsible Spend Chart ────────────────────────────────────── */}
          {spendChartMonthly.length > 0 && availableMonths.length > 0 && (() => {
            const C_MPY = "#0891b2";
            const C_ANNUAL = "#e85d04";

            let cMpy = 0, cAnnual = 0;
            const chartData = spendChartMonthly.map(d => {
              const mpy = d.mActual !== null ? d.mActual : d.mBudgetForecast;
              cMpy += mpy;
              cAnnual += d.mBudgetAnnual;
              return {
                name: d.short,
                mpy,
                annualBudget: d.mBudgetAnnual,
                cMpy,
                cAnnual,
              };
            });

            const fmtAxis = (v: number) => { const a = Math.abs(v); return v === 0 ? "$0" : a >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : a >= 1e3 ? `$${(v / 1e3).toFixed(0)}k` : `$${Math.round(v)}`; };
            const fmtBarLabel = (v: any) => {
              const n = Number(v);
              if (!n) return "";
              const abs = Math.abs(n);
              return abs >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : abs >= 1e3 ? `$${(n / 1e3).toFixed(0)}k` : `$${Math.round(n)}`;
            };
            const fmtTipVal = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

            const CustomTooltip = ({ active, payload, label }: any) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="dash-recharts-tooltip">
                  <div className="dash-recharts-tooltip-label">{label}</div>
                  {payload.map((entry: any, i: number) => (
                    <div key={i} className="dash-recharts-tooltip-row">
                      <span className="dash-recharts-tooltip-dot" style={{ background: entry.color }} />
                      <span className="dash-recharts-tooltip-name">{entry.name}</span>
                      <span className="dash-recharts-tooltip-val">{fmtTipVal(entry.value)}</span>
                    </div>
                  ))}
                </div>
              );
            };

            const SeriesLegend = ({ payload }: any) => (
              <div className="dash-chart-legend-custom">
                {payload?.map((entry: any, i: number) => (
                  <span key={i} className="dash-chart-legend-item">
                    <span className="dash-chart-legend-dot" style={{ background: entry.color }} />
                    {entry.value}
                  </span>
                ))}
              </div>
            );

            return (
              <div className="dash-chart-card dash-chart-card--collapsible">
                <div
                  className="dash-chart-header--plot"
                  onClick={() => setChartCollapsed(!chartCollapsed)}
                >
                  <span className="material-icons" style={{ fontSize: 22, color: "#0891b2" }}>
                    {chartType === "bar" ? "bar_chart" : "show_chart"}
                  </span>
                  <span style={{ fontWeight: 800, fontSize: 14, color: "oklch(0.22 0.03 250)", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    Monthly MPY vs Annual Budget
                  </span>
                  {chartCollapsed && (
                    <span style={{ marginLeft: "auto", fontSize: 12, color: "#0891b2", fontWeight: 600, display: "flex", alignItems: "center", gap: 5, background: "oklch(0.92 0.04 200)", padding: "4px 10px", borderRadius: 6 }}>
                      <span className="material-icons" style={{ fontSize: 16 }}>visibility</span>
                      Click to view chart
                    </span>
                  )}
                  <span className="material-icons" style={{ marginLeft: chartCollapsed ? 6 : "auto", fontSize: 22, color: "#0891b2" }}>
                    {chartCollapsed ? "expand_more" : "expand_less"}
                  </span>
                </div>
                {!chartCollapsed && (
                  <div className="dash-chart-body">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div />
                      <div className="dash-chart-toggle">
                        <button
                          className={`dash-chart-toggle-btn${chartType === "bar" ? " dash-chart-toggle-btn--active" : ""}`}
                          onClick={() => setChartType("bar")}
                        >
                          <span className="material-icons" style={{ fontSize: 16 }}>bar_chart</span>
                          Bar
                        </button>
                        <button
                          className={`dash-chart-toggle-btn${chartType === "line" ? " dash-chart-toggle-btn--active" : ""}`}
                          onClick={() => setChartType("line")}
                        >
                          <span className="material-icons" style={{ fontSize: 16 }}>show_chart</span>
                          Line
                        </button>
                      </div>
                    </div>

                    <div className="dash-chart-cat-strip">
                      <button
                        className={`dash-chart-cat-chip${selectedCategory === null ? " dash-chart-cat-chip--active" : ""}`}
                        onClick={() => setSelectedCategory(null)}
                      >
                        All
                      </button>
                      {categorySummaries.map(cat => {
                        const color = CATEGORY_COLORS[cat.category] ?? "#64748b";
                        const isActive = selectedCategory === cat.category;
                        return (
                          <button
                            key={cat.category}
                            className={`dash-chart-cat-chip${isActive ? " dash-chart-cat-chip--active" : ""}`}
                            style={isActive ? { background: color + "14", color, borderColor: color + "60" } : undefined}
                            onClick={() => setSelectedCategory(isActive ? null : cat.category)}
                          >
                            <span className="dash-chart-cat-chip-dot" style={{ background: color }} />
                            {cat.category}
                          </button>
                        );
                      })}
                    </div>

                    {selectedAccount && (
                      <div className="dash-acct-filter-strip">
                        <span className="material-icons" style={{ fontSize: 14, color: "#4A90E2" }}>filter_alt</span>
                        <span className="dash-acct-filter-label">Showing: <strong>{selectedAccount}</strong></span>
                        <button className="dash-acct-filter-clear" onClick={() => setSelectedAccount(null)} title="Clear account filter">
                          <span className="material-icons" style={{ fontSize: 14 }}>close</span>
                        </button>
                      </div>
                    )}

                    <div className="dash-chart-body">
                      <ResponsiveContainer width="100%" height={240}>
                        {chartType === "bar" ? (
                          <ComposedChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 0 }} barCategoryGap="20%">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                            <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 12, fill: "#64748b" }} width={56} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: "oklch(0.97 0.005 260)" }} />
                            <Legend content={<SeriesLegend />} />
                            <Bar dataKey="mpy" name="MPY (Actuals + Forecast)" fill={C_MPY} radius={[3, 3, 0, 0]}>
                              <LabelList dataKey="mpy" position="top" formatter={fmtBarLabel} style={{ fontSize: 11, fill: C_MPY, fontWeight: 600 }} />
                            </Bar>
                            <Line type="monotone" dataKey="annualBudget" name="Annual Budget" stroke={C_ANNUAL} strokeWidth={2.5} dot={{ r: 3.5, fill: C_ANNUAL, strokeWidth: 1.5, stroke: "#fff" }} />
                          </ComposedChart>
                        ) : (
                          <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                            <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 12, fill: "#64748b" }} width={56} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend content={<SeriesLegend />} />
                            <Line type="monotone" dataKey="cMpy" name="MPY (Cumulative)" stroke={C_MPY} strokeWidth={2.5} dot={{ r: 3, fill: C_MPY, strokeWidth: 1.5, stroke: "#fff" }} />
                            <Line type="monotone" dataKey="cAnnual" name="Annual Budget (Cumulative)" stroke={C_ANNUAL} strokeWidth={2.5} dot={false} strokeDasharray="6 3" />
                          </LineChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Category KPI Cards ─────────────────────────────────────────── */}
          <div className="dash-cat-strip">

            {/* Total card */}
            <div
              className={`dash-cat-card dash-cat-card--total${selectedCategory === null ? " dash-cat-card--active" : ""}`}
              onClick={() => setSelectedCategory(null)}
              style={{ cursor: "pointer" }}
            >
              <div className="dash-cat-name">Total Spend</div>
              <div className="dash-cat-rows">
                <div className="dash-cat-row">
                  <span className="dash-cat-row-label">{refDimLabel ?? "Forecast"}</span>
                  <span className="dash-cat-row-val">{fmt(totalBudget)}</span>
                </div>
                <div className="dash-cat-row">
                  <span className="dash-cat-row-label">Actuals</span>
                  <span className="dash-cat-row-val">{fmt(totalActual)}</span>
                </div>
                <div className={`dash-cat-row dash-cat-row--var ${totalVariance >= 0 ? "dash-fav" : "dash-unfav"}`}>
                  <span className="dash-cat-row-label">Variance</span>
                  <span className="dash-cat-row-val">{fmtVar(totalVariance)}</span>
                </div>
                <div className={`dash-cat-row dash-cat-row--var ${ytdVariance >= 0 ? "dash-fav" : "dash-unfav"}`}>
                  <span className="dash-cat-row-label">YTD Variance</span>
                  <span className="dash-cat-row-val">{fmtVar(ytdVariance)}</span>
                </div>
              </div>
              <div className="dash-progress-wrap">
                <div
                  className="dash-progress-fill"
                  style={{
                    transform: `scaleX(${Math.min(100, totalUtilPct) / 100})`,
                    background: totalActual > fullYearBudget ? "#dc2626" : "#16a34a",
                  }}
                />
              </div>
              <div className="dash-progress-label">
                {Math.round(totalUtilPct)}% of forecast utilized
              </div>
            </div>

            {/* Divider between Total and category cards */}
            <div className="dash-cat-divider" />

            {/* Per-category cards */}
            {categorySummaries.map(cat => {
              const color     = CATEGORY_COLORS[cat.category] ?? "#64748b";
              const utilPct   = cat.budget > 0 ? (cat.actual / cat.budget) * 100 : 0;
              const isActive  = selectedCategory === cat.category;
              return (
                <div
                  key={cat.category}
                  className={`dash-cat-card${isActive ? " dash-cat-card--active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedCategory(isActive ? null : cat.category)}
                >
                  <div className="dash-cat-name"><span className="dash-cat-dot" style={{ background: color }} />{cat.category}</div>
                  <div className="dash-cat-rows">
                    <div className="dash-cat-row">
                      <span className="dash-cat-row-label">{refDimLabel ?? "Forecast"}</span>
                      <span className="dash-cat-row-val">{fmt(cat.budget)}</span>
                    </div>
                    <div className="dash-cat-row">
                      <span className="dash-cat-row-label">Actuals</span>
                      <span className="dash-cat-row-val">{fmt(cat.actual)}</span>
                    </div>
                    <div className={`dash-cat-row dash-cat-row--var ${cat.variance >= 0 ? "dash-fav" : "dash-unfav"}`}>
                      <span className="dash-cat-row-label">Variance</span>
                      <span className="dash-cat-row-val">{fmtVar(cat.variance)}</span>
                    </div>
                    <div className={`dash-cat-row dash-cat-row--var ${cat.ytdVariance >= 0 ? "dash-fav" : "dash-unfav"}`}>
                      <span className="dash-cat-row-label">YTD Variance</span>
                      <span className="dash-cat-row-val">{fmtVar(cat.ytdVariance)}</span>
                    </div>
                  </div>
                  <div className="dash-progress-wrap">
                    <div
                      className="dash-progress-fill"
                      style={{
                        transform: `scaleX(${Math.min(100, utilPct) / 100})`,
                        background: utilPct > 100 ? "#dc2626" : color,
                      }}
                    />
                  </div>
                  <div className="dash-progress-label">{Math.round(utilPct)}% utilized</div>
                </div>
              );
            })}
          </div>

          {/* ── Admin Monthly Comment (read-only for CC owners) ───────── */}
          {adminComment && (
            <div style={{
              background: "oklch(0.97 0.012 220)",
              border: "1px solid oklch(0.88 0.02 220)",
              borderRadius: 8,
              padding: "14px 18px",
              marginBottom: 16,
              marginTop: 4,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.50 0.12 250)" }}>rate_review</span>
                <span style={{ fontWeight: 600, fontSize: 13, color: "oklch(0.30 0.03 250)" }}>Finance Assessment — {selectedMonth} {selectedFY ? selectedFY.toUpperCase() : ""}</span>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: "oklch(0.25 0.02 250)", whiteSpace: "pre-wrap" }}>
                {adminComment}
              </div>
            </div>
          )}

          {/* ── Account Tables ─────────────────────────────────────────────── */}
          <div className="dash-tables-section">
            <div className="dash-tables-section-header">
              <span className="material-icons">assessment</span>
              <span className="dash-tables-section-title">Current Forecast Status</span>
            </div>
            <div className="dash-tables-section-inner">
          <div className="dash-tables-row">

            {/* Over Budget */}
            <div className="dash-table-card dash-table-card--unfav">
              <div className="dash-table-head">
                <span className="material-icons">trending_up</span>
                Top 5 Over Forecast
                {selectedCategory && (
                  <span className="dash-table-filter-badge" style={{ background: (CATEGORY_COLORS[selectedCategory] ?? "#64748b") + "22", color: CATEGORY_COLORS[selectedCategory] ?? "#64748b" }}>
                    {selectedCategory}
                  </span>
                )}
              </div>
              <table className="dash-tbl">
                <thead>
                  <tr>
                    <th className="dash-th-left">Account</th>
                    <th>Forecast</th>
                    <th>Actuals</th>
                    <th>Variance</th>
                    <th>YTD Variance</th>
                    <th className="dash-th-center">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {top5Unfavorable.map((row, i) => {
                    const color = CATEGORY_COLORS[row.category] ?? "#64748b";
                    return (
                      <tr key={row.account} className={i % 2 === 1 ? "dash-tr-alt" : ""}>
                        <td className="dash-td-account" title={row.account}>{row.account}</td>
                        <td className="dash-td-num">{fmt(row.budget)}</td>
                        <td className="dash-td-num">{fmt(row.actual)}</td>
                        <td className="dash-td-num dash-unfav">{fmtVar(row.variance)}</td>
                        <td className={`dash-td-num ${row.ytdVariance < 0 ? "dash-unfav" : "dash-fav"}`}>{fmtVar(row.ytdVariance)}</td>
                        <td className="dash-td-center">
                          <span className="dash-badge" style={{ background: color + "18", color }}>
                            {row.category}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {top5Unfavorable.length === 0 && (
                    <tr>
                      <td colSpan={6} className="dash-td-empty">
                        <span className="material-icons">check_circle</span>
                        All accounts within forecast
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Under Budget */}
            <div className="dash-table-card dash-table-card--fav">
              <div className="dash-table-head">
                <span className="material-icons">trending_down</span>
                Top 5 Under Forecast
                {selectedCategory && (
                  <span className="dash-table-filter-badge" style={{ background: (CATEGORY_COLORS[selectedCategory] ?? "#64748b") + "22", color: CATEGORY_COLORS[selectedCategory] ?? "#64748b" }}>
                    {selectedCategory}
                  </span>
                )}
              </div>
              <table className="dash-tbl">
                <thead>
                  <tr>
                    <th className="dash-th-left">Account</th>
                    <th>Forecast</th>
                    <th>Actuals</th>
                    <th>Variance</th>
                    <th>YTD Variance</th>
                    <th className="dash-th-center">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {top5Favorable.map((row, i) => {
                    const color = CATEGORY_COLORS[row.category] ?? "#64748b";
                    return (
                      <tr key={row.account} className={i % 2 === 1 ? "dash-tr-alt" : ""}>
                        <td className="dash-td-account" title={row.account}>{row.account}</td>
                        <td className="dash-td-num">{fmt(row.budget)}</td>
                        <td className="dash-td-num">{fmt(row.actual)}</td>
                        <td className="dash-td-num dash-fav">{fmtVar(row.variance)}</td>
                        <td className={`dash-td-num ${row.ytdVariance < 0 ? "dash-unfav" : "dash-fav"}`}>{fmtVar(row.ytdVariance)}</td>
                        <td className="dash-td-center">
                          <span className="dash-badge" style={{ background: color + "18", color }}>
                            {row.category}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {top5Favorable.length === 0 && (
                    <tr>
                      <td colSpan={6} className="dash-td-empty">
                        <span className="material-icons">info</span>
                        No under-forecast accounts
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
          </div>{/* /dash-tables-section-inner */}
          </div>{/* /dash-tables-section */}

          {/* ── Category Variance (matching ActualsDashboard style) ────────── */}
          {categoryVarData.length > 0 && (() => {
            const periodLabel = selectedMonth === "YTD" ? "YTD" : selectedMonth;
            const utilLabel   = quarterLabel || "YTD";
            return (
              <div className="dash-cat-table-wrap">
                <div className="ccx-header">
                  <span className="material-icons" style={{ fontSize: "16px", color: "#6366f1" }}>account_tree</span>
                  <span className="ccx-title">Category Variance</span>
                  <span className="ccx-subtitle">{periodLabel} · click a row to see account breakdown</span>
                  <span className="ccx-count">{categoryVarData.length} categories</span>
                  <button
                    onClick={() => setDashSimpleView(v => !v)}
                    className={`excel-mode-btn${dashSimpleView ? " excel-mode-btn--active" : ""}`}
                    title={dashSimpleView ? "Show all accounts including $0 lines" : "Hide accounts with $0 budget and actuals"}
                    style={{ marginLeft: "auto", fontSize: 10, padding: "3px 8px", gap: 4 }}
                  >
                    <span className="material-icons" style={{ fontSize: 14 }}>{dashSimpleView ? "visibility_off" : "visibility"}</span>
                    {dashSimpleView ? "Show All" : "Simplified"}
                  </button>
                  <div ref={dashAcctDropRef} style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", position: "relative" }}>
                    {dashAcctTags.map((tag, i) => (
                      <span key={i} style={{
                        display: "inline-flex", alignItems: "center", gap: 3,
                        background: "oklch(0.92 0.04 260)", color: "oklch(0.30 0.08 260)",
                        borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 600,
                      }}>
                        {tag}
                        <span className="material-icons" style={{ fontSize: 12, cursor: "pointer", color: "oklch(0.50 0.06 260)" }} onClick={() => setDashAcctTags(prev => prev.filter((_, j) => j !== i))}>close</span>
                      </span>
                    ))}
                    <span className="material-icons" style={{ fontSize: 15, color: "oklch(0.55 0.02 250)" }}>search</span>
                    <input
                      ref={dashAcctInputRef}
                      type="text"
                      placeholder={dashAcctTags.length > 0 ? "Add more…" : "Filter accounts…"}
                      value={dashAcctSearch}
                      onChange={e => { setDashAcctSearch(e.target.value); setDashAcctDropOpen(true); }}
                      onFocus={() => setDashAcctDropOpen(true)}
                      onKeyDown={e => {
                        if (e.key === "Backspace" && !dashAcctSearch && dashAcctTags.length > 0) setDashAcctTags(prev => prev.slice(0, -1));
                        else if (e.key === "Escape") setDashAcctDropOpen(false);
                      }}
                      style={{ width: 170, padding: "4px 8px", fontSize: 11, fontWeight: 500, border: "1px solid oklch(0.85 0.01 250)", borderRadius: 6, background: "oklch(0.98 0.003 250)", color: "oklch(0.20 0.02 250)", outline: "none" }}
                    />
                    {dashAcctTags.length > 0 && (
                      <button onClick={() => { setDashAcctTags([]); setDashAcctSearch(""); }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", padding: 2 }} title="Clear all filters">
                        <span className="material-icons" style={{ fontSize: 14, color: "oklch(0.55 0.02 250)" }}>close</span>
                      </button>
                    )}
                    {dashAcctDropOpen && dashAcctSuggestions.length > 0 && (
                      <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 4, background: "oklch(0.99 0.002 250)", border: "1px solid oklch(0.88 0.01 250)", borderRadius: 8, boxShadow: "0 6px 20px oklch(0.20 0.02 250 / 0.15)", zIndex: 50, minWidth: 280, maxHeight: 260, overflowY: "auto" }}>
                        {dashAcctSuggestions.map(a => {
                          const already = dashAcctTags.includes(a.account);
                          return (
                            <div key={a.account} onClick={() => { if (!already) setDashAcctTags(prev => [...prev, a.account]); setDashAcctSearch(""); setDashAcctDropOpen(false); dashAcctInputRef.current?.focus(); }}
                              style={{ padding: "6px 12px", fontSize: 11, cursor: already ? "default" : "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", color: already ? "oklch(0.65 0.02 250)" : "oklch(0.20 0.02 250)", background: already ? "oklch(0.96 0.01 250)" : "transparent" }}
                              onMouseEnter={e => { if (!already) e.currentTarget.style.background = "oklch(0.95 0.02 260)"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = already ? "oklch(0.96 0.01 250)" : "transparent"; }}
                            >
                              <span>{a.account}</span>
                              <span style={{ fontSize: 9, color: "oklch(0.55 0.02 250)", fontWeight: 500 }}>{a.category}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <table className="dash-cat-table dash-var-table">
                  <thead>
                    <tr>
                      <th className="dash-cat-th-left dash-var-col--left">Category</th>
                      <th className="dash-var-col--num">{periodLabel} Forecast</th>
                      <th className="dash-var-col--num">{periodLabel} Actuals</th>
                      <th className="dash-var-col--num">{periodLabel} Variance</th>
                      <th className="dash-var-col--num">YTD Actuals</th>
                      <th className="dash-var-col--num">YTD Variance</th>
                      <th className="dash-var-col--num" style={{ textAlign: "center" }}>MPY</th>
                      <th className="dash-var-col--num" style={{ textAlign: "center" }}>MPY Variance</th>
                      <th className="dash-var-col--bar">Account Breakdown ({periodLabel})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryVarData.map(cat => {
                      const acctTagsLower = dashAcctTags.map(t => t.toLowerCase());
                      const filteredAccounts = dashAcctTags.length > 0
                        ? cat.accounts.filter(r => acctTagsLower.some(t => r.account.toLowerCase().includes(t)))
                        : cat.accounts;
                      if (dashAcctTags.length > 0 && filteredAccounts.length === 0) return null;
                      const hasFilt = dashAcctTags.length > 0;
                      const fBudget = hasFilt ? filteredAccounts.reduce((s, r) => s + r.budget, 0) : cat.budget;
                      const fActual = hasFilt ? filteredAccounts.reduce((s, r) => s + r.actual, 0) : cat.actual;
                      const fVariance = fBudget - fActual;
                      const fYtdActual = hasFilt ? filteredAccounts.reduce((s, r) => s + (r.ytdActual || 0), 0) : cat.ytdActual;
                      const fYtdBudget = hasFilt ? filteredAccounts.reduce((s, r) => s + (r.ytdBudget || 0), 0) : cat.ytdBudget;
                      const fYtdVariance = fYtdBudget - fYtdActual;
                      if (dashSimpleView && !fBudget && !fActual && !fYtdActual && !fYtdBudget) return null;
                      const color = CATEGORY_COLORS[cat.category] ?? "#64748b";
                      const isExpanded = expandedCategories.has(cat.category);
                      return (
                        <React.Fragment key={cat.category}>
                          <tr
                            className={`dash-cat-tr${isExpanded ? " dash-cat-tr--active" : ""}`}
                            onClick={() => {
                              const next = new Set(expandedCategories);
                              if (isExpanded) next.delete(cat.category); else next.add(cat.category);
                              setExpandedCategories(next);
                            }}
                            title={`Click to see ${cat.category} accounts`}
                          >
                            <td className="dash-cat-td-name">
                              <span className="material-icons dash-cat-expand-icon">{isExpanded ? "expand_less" : "expand_more"}</span>
                              <span className="dash-cat-dot" style={{ background: color }} />
                              {cat.category}
                            </td>
                            <td className="dash-cat-td-num">{fmt(fBudget)}</td>
                            <td className="dash-cat-td-num">{fmt(fActual)}</td>
                            <td className={`dash-cat-td-num ${fVariance >= 0 ? "dash-fav" : "dash-unfav"}`}>
                              {fmtVar(fVariance)}
                            </td>
                            <td className="dash-cat-td-num">{fmt(fYtdActual)}</td>
                            <td className={`dash-cat-td-num ${fYtdVariance < 0 ? "dash-unfav" : "dash-fav"}`}>
                              {fmtVar(fYtdVariance)}
                            </td>
                            <td className="dash-cat-td-num" style={{ textAlign: "center" }}>
                              {fmt(cat.catMPY)}
                            </td>
                            {(() => {
                              const mv = cat.catAnnBudget - cat.catMPY;
                              return (
                                <td className={`dash-cat-td-num ${mv < 0 ? "dash-unfav" : "dash-fav"}`} style={{ textAlign: "center" }}>
                                  {fmtVar(mv)}
                                </td>
                              );
                            })()}
                            <td className="ccx-td-breakdown">
                              {cat.acctVars.length > 0 ? (
                                <div className="ccx-bar-stack">
                                  {cat.acctVars.map(av => {
                                    const pct = Math.abs(av.variance) / cat.totalAbsVar * 100;
                                    if (pct < 3) return null;
                                    return (
                                      <div
                                        key={av.account}
                                        className="ccx-bar-seg"
                                        style={{ width: `${pct}%`, background: color, opacity: av.variance > 0 ? 1 : 0.4 }}
                                        title={`${av.account}: ${fmtVar(av.variance)}`}
                                      />
                                    );
                                  })}
                                </div>
                              ) : (
                                <span style={{ color: "#9ca3af", fontSize: "11px" }}>{"\u2014"}</span>
                              )}
                            </td>
                          </tr>

                          {/* Expanded: account rows flattened into parent table */}
                          {isExpanded && filteredAccounts.filter(row => !dashSimpleView || row.budget || row.actual || row.ytdActual || row.ytdBudget).map(row => {
                            const rowVar = row.budget - row.actual;
                            const isAcctSelected = selectedAccount === row.account;
                            const acctMPY = mpyByAcct[row.account] || 0;
                            const acctAB = abByAcct[row.account] || 0;
                            const acctMpyVar = acctAB - acctMPY;
                            return (
                              <React.Fragment key={`${cat.category}-${row.account}`}>
                              <tr
                                className={`ccx-acct-row${isAcctSelected ? " ccx-cat-row--selected" : ""}`}
                                onClick={(e) => { e.stopPropagation(); setSelectedAccount(isAcctSelected ? null : row.account); }}
                                title={isAcctSelected ? "Click to clear account filter" : "Click to filter chart by this account"}
                              >
                                <td className="dash-cat-td-name" style={{ paddingLeft: 28, fontWeight: 400, fontSize: "12px" }}>
                                  {isAcctSelected && <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4, color: CATEGORY_COLORS[cat.category] ?? "#4A90E2" }}>filter_alt</span>}
                                  {row.account}
                                </td>
                                <td className="dash-cat-td-num">{fmt(row.budget)}</td>
                                <td className="dash-cat-td-num">{fmt(row.actual)}</td>
                                <td className={`dash-cat-td-num ${rowVar >= 0 ? "dash-fav" : "dash-unfav"}`}>
                                  {fmtVar(rowVar)}
                                </td>
                                <td className="dash-cat-td-num">{fmt(row.ytdActual)}</td>
                                <td className={`dash-cat-td-num ${(row.ytdBudget - row.ytdActual) < 0 ? "dash-unfav" : "dash-fav"}`}>
                                  {fmtVar(row.ytdBudget - row.ytdActual)}
                                </td>
                                <td className="dash-cat-td-num" style={{ textAlign: "center" }}>{fmt(acctMPY)}</td>
                                <td className={`dash-cat-td-num ${acctMpyVar < 0 ? "dash-unfav" : "dash-fav"}`} style={{ textAlign: "center" }}>
                                  {fmtVar(acctMpyVar)}
                                </td>
                                <td className="dash-cat-td-num" style={{ fontSize: "11px", color: "#64748b" }}>
                                  {fmtVar(rowVar)}
                                </td>
                              </tr>
                              {isAcctSelected && (() => {
                                const acctChartData = FISCAL_MONTHS_ALL.map((m, i) => {
                                  const dim = MONTH_BUDGET_DIM[m] ?? "Annual Budget";
                                  const dimData = budgetsByDim[dim] ?? {};
                                  const forecastVal = Number(dimData[row.account]?.find((e: any) => e.month === m)?.budget) || 0;
                                  const annualBudgetDim = budgetsByDim["Annual Budget"] ?? {};
                                  const annualVal = Number(annualBudgetDim[row.account]?.find((e: any) => e.month === m)?.budget) || 0;
                                  const actualVal = Number(actualsRaw[row.account]?.find((e: any) => e.month === m)?.budget) || 0;
                                  const hasActual = availableMonths.includes(m);
                                  const mpy = hasActual ? actualVal : forecastVal;
                                  return { name: FISCAL_MONTHS_SHORT[i], mpy, annualBudget: annualVal, variance: annualVal - mpy };
                                });
                                const acctFmtAxis = (v: number) => v === 0 ? "$0" : Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `$${(v / 1e3).toFixed(0)}k` : `$${Math.round(v)}`;
                                const acctFmtTip = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
                                const AcctTooltip = ({ active, payload, label }: any) => {
                                  if (!active || !payload?.length) return null;
                                  return (
                                    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
                                      {payload.filter((e: any) => e.value !== null).map((entry: any, i: number) => (
                                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color }} />
                                          <span>{entry.name}:</span>
                                          <span style={{ fontWeight: 600 }}>{acctFmtTip(entry.value)}</span>
                                        </div>
                                      ))}
                                      {payload[0]?.payload?.variance !== null && (
                                        <div style={{ marginTop: 4, borderTop: "1px solid #f1f5f9", paddingTop: 4, fontWeight: 600, color: payload[0].payload.variance >= 0 ? "oklch(0.45 0.15 145)" : "oklch(0.50 0.18 25)" }}>
                                          Variance: {payload[0].payload.variance < 0 ? `(${acctFmtTip(Math.abs(payload[0].payload.variance))})` : acctFmtTip(payload[0].payload.variance)}
                                        </div>
                                      )}
                                    </div>
                                  );
                                };
                                return (
                                  <tr>
                                    <td colSpan={9} style={{ padding: "12px 16px 16px", background: "oklch(0.97 0.005 250)" }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: "oklch(0.35 0.02 250)", marginBottom: 8 }}>
                                        {row.account} — Monthly MPY vs Annual Budget
                                      </div>
                                      <ResponsiveContainer width="100%" height={200}>
                                        <ComposedChart data={acctChartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barCategoryGap="20%">
                                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                                          <YAxis axisLine={false} tickLine={false} tickFormatter={acctFmtAxis} tick={{ fontSize: 12, fill: "#64748b" }} width={56} />
                                          <Tooltip content={<AcctTooltip />} cursor={{ fill: "oklch(0.97 0.005 260)" }} />
                                          <Legend />
                                          <Bar dataKey="mpy" name="MPY" fill="#0891b2" radius={[3, 3, 0, 0]} />
                                          <Line type="monotone" dataKey="annualBudget" name="Annual Budget" stroke="#4A90E2" strokeWidth={2} dot={{ r: 3, fill: "#4A90E2", strokeWidth: 1.5, stroke: "#fff" }} />
                                        </ComposedChart>
                                      </ResponsiveContainer>
                                    </td>
                                  </tr>
                                );
                              })()}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {(() => {
                    const acctTagsLower = dashAcctTags.map(t => t.toLowerCase());
                    const totals = categoryVarData.reduce((t, cat) => {
                      const rows = dashAcctTags.length > 0
                        ? cat.accounts.filter(r => acctTagsLower.some(tag => r.account.toLowerCase().includes(tag)))
                        : cat.accounts;
                      if (dashAcctTags.length > 0 && rows.length === 0) return t;
                      const hasFilt = dashAcctTags.length > 0;
                      return {
                        budget: t.budget + (hasFilt ? rows.reduce((s, r) => s + r.budget, 0) : cat.budget),
                        actual: t.actual + (hasFilt ? rows.reduce((s, r) => s + r.actual, 0) : cat.actual),
                        ytdActual: t.ytdActual + (hasFilt ? rows.reduce((s, r) => s + (r.ytdActual || 0), 0) : cat.ytdActual),
                        ytdBudget: t.ytdBudget + (hasFilt ? rows.reduce((s, r) => s + (r.ytdBudget || 0), 0) : cat.ytdBudget),
                        mpy: t.mpy + cat.catMPY,
                        annBudget: t.annBudget + cat.catAnnBudget,
                      };
                    }, { budget: 0, actual: 0, ytdActual: 0, ytdBudget: 0, mpy: 0, annBudget: 0 });
                    const tVar = totals.budget - totals.actual;
                    const tYtdVar = totals.ytdBudget - totals.ytdActual;
                    const tMpyVar = totals.annBudget - totals.mpy;
                    const varCls = (v: number) => v >= 0 ? "dash-fav" : "dash-unfav";
                    return (
                      <tfoot>
                        <tr style={{ background: "oklch(0.92 0.03 250)", fontWeight: 800 }}>
                          <td style={{ padding: "8px 10px", color: "oklch(0.25 0.06 250)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>Total</td>
                          <td className="dash-cat-td-num" style={{ color: "oklch(0.25 0.06 250)" }}>{fmt(totals.budget)}</td>
                          <td className="dash-cat-td-num" style={{ color: "oklch(0.25 0.06 250)" }}>{fmt(totals.actual)}</td>
                          <td className={`dash-cat-td-num ${varCls(tVar)}`}>{fmtVar(tVar)}</td>
                          <td className="dash-cat-td-num" style={{ color: "oklch(0.25 0.06 250)" }}>{fmt(totals.ytdActual)}</td>
                          <td className={`dash-cat-td-num ${varCls(tYtdVar)}`}>{fmtVar(tYtdVar)}</td>
                          <td className="dash-cat-td-num" style={{ color: "oklch(0.25 0.06 250)", textAlign: "center" }}>{fmt(totals.mpy)}</td>
                          <td className={`dash-cat-td-num ${varCls(tMpyVar)}`} style={{ textAlign: "center" }}>{fmtVar(tMpyVar)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    );
                  })()}
                </table>
              </div>
            );
          })()}

        </>
      )}

      {!loading && !selectedMonth && availableMonths.length === 0 && (
        <div className="dash-empty-state">
          <span className="material-icons">bar_chart</span>
          <div>No actuals data available yet for this cost center.</div>
          <div>Upload actuals to view the dashboard.</div>
        </div>
      )}

      {/* Action Plan slide-in panel */}
      <ActionPlanPanel
        open={actionPlanOpen}
        onClose={() => { setActionPlanOpen(false); fetchActionCount(); }}
        costCenterNumber={costCenterNumber}
        costCenterName={costCenterName}
        fiscalYear={selectedFY}
        month={selectedMonth}
        categories={CATEGORY_ORDER}
        accounts={accountRows.map(r => ({ account: r.account, category: r.category }))}
      />

    </div>
  );
};

export default Dashboard;
